<script setup>
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, toRef } from 'vue';

import { useCarDetails } from '@/composables/useCarDetails.js';
import { useDataFormat } from '@/composables/useDataFormat.js';
import { useFormatLicensePlate } from '@/composables/useFormatLicensePlate.js';
import { usePrefillParams } from '@/composables/useUrlParams.js';
import { regexLicensePlate } from '@/config/config.formkitValidationRules.js';
import { formStore } from '@/stores/form.js';
import { voertuigStore } from '@/stores/voertuig.js';

const { capitalizeFirstLetter } = useDataFormat();
const emit = defineEmits(['onchange']);
const props = defineProps({
  voertuig: {
    type: Object,
    default: () => ({}),
  },
  typeIdOriginal: {
    type: String,
    default: '',
  },
  kentekenOnbekend: {
    type: Boolean,
    default: false,
  },
});

toRef(props);

const usevoertuigStore = voertuigStore();
const validRegexLicensePlate = ref(false);
const calculatedSoortFromApi = ref('');
const funnelFromApi = ref('');
const { formatLicensePlate } = useFormatLicensePlate();
const { otherVehicleTypeLink } = useCarDetails(props.voertuig);
const useFormStore = formStore();
let { funnelType, localKenteken, voertuigSoortOriginal, startedWithPrefills } = storeToRefs(formStore());
const licensePlateRequired = computed(() => {
  return props.kentekenOnbekend ? '' : 'required';
});

const wrongLicensePlate = computed(() => {
  return regexLicensePlate && props.voertuig.soort === 'Onbekend';
});

const isOtherVehicleType = () => {
  return (
    voertuigSoortOriginal.value !== funnelFromApi.value &&
    !(voertuigSoortOriginal.value === 'auto' && (funnelFromApi.value === 'personenauto' || funnelFromApi.value === 'bestelauto'))
  );
};

const atInput = (e) => {
  // input length > 0
  if (e === null) return;

  // input did not change
  if (e === localKenteken) return;

  // format licenseplate
  const kentekenFormatted = formatLicensePlate(e);
  const localKentekenFormatted = formatLicensePlate(localKenteken);

  // input did change but is exact the same as the formatted value
  if (localKentekenFormatted === kentekenFormatted) return;

  // doublecheck regex of formatted licenseplate
  validRegexLicensePlate.value = regexLicensePlate({ value: kentekenFormatted });

  if (!validRegexLicensePlate.value) return;

  // if licenseplate regex is true
  if (validRegexLicensePlate.value) {
    // fetch the vehicle from the API
    usevoertuigStore.fetchVoertuigData(kentekenFormatted).then((response) => {
      funnelFromApi.value = response.berekende_funnel;
      // EMIT voor button disable als voertuigsoort anders is
      emit('onchange', isOtherVehicleType());

      // set type_id_correct if type_id_correct did not change
      if (props.voertuig.kentekenOnbekend) {
        response.type_id_correct = props.voertuig.type_id === '' || props.voertuig.type_id === response.type_id;
      }
      // set the right vehicle type from the api results
      calculatedSoortFromApi.value = response.berekende_soort.toLowerCase();

      // Als isOtherVehicleType false is (Dus voertuigsoort klopt), dan word voertuidata geupdate
      if (!isOtherVehicleType()) {
        // Update the vehicle object only if the licenseplate is for the right funnel type
        response.soort = response.berekende_soort;
        Object.assign(props.voertuig, JSON.parse(JSON.stringify(response)));

        // update the local licenseplate value
        localKenteken.value = kentekenFormatted;
      } else {
        // if vehicle type is not correct, set loader to false
        useFormStore.setSkeletonLoader(false);
      }
    });
  }
};

const checkPrefill = () => {
  const { newKenteken } = usePrefillParams();

  if (newKenteken && startedWithPrefills.value) {
    atInput(newKenteken.replace(/^\/+|\/+$/g, ''));
    return true;
  } else {
    return false;
  }
};

onMounted(() => {
  if (!checkPrefill()) {
    const licence = props.voertuig.kenteken ?? '';
    localKenteken.value = formatLicensePlate(licence);
  }
  voertuigSoortOriginal.value = funnelType;

  funnelFromApi.value = props.voertuig.berekende_funnel !== '' ? props.voertuig.berekende_funnel : voertuigSoortOriginal.value;

  calculatedSoortFromApi.value =
    props.voertuig.berekende_soort !== '' ? props.voertuig.berekende_soort : props.voertuig.soort.toLowerCase();
});
</script>

<template>
  <div>
    <BaseInput
      :name="$t('start-calculation.vehicleInfo.licensePlate')"
      :label="$t('start-calculation.vehicleInfo.licensePlate')"
      :model-value="localKenteken"
      license-plate
      :validation="[[licensePlateRequired], [regexLicensePlate]]"
      validation-visibility="blur"
      :disabled="kentekenOnbekend"
      @input="atInput($event)"
    />

    <div v-if="localKenteken !== '' && props.voertuig.merk !== ''">
      {{ capitalizeFirstLetter($t('start-calculation.vehicleInfo.brand')) + ':' }}
      <label data-wg-notranslate>{{ props.voertuig.merk }}</label>
    </div>
    <!-- props.voertuig.getHasVehicle WON'T work when BaseLicensePlate is loaded with only the state (such as in AlmostInsured.vue) 
    This component needs refactoring before being able to fix this problem. i.e. use seperate props -->
    <div v-if="localKenteken !== '' && props.voertuig.getHasVehicle">
      {{ capitalizeFirstLetter($t('start-calculation.vehicleInfo.type')) + ':' }}
      <label data-wg-notranslate>{{ props.voertuig.getType }}</label>
    </div>

    <div v-if="isOtherVehicleType()">
      <BaseToastMessage
        hide-arrow
        color="warning"
      >
        <BaseIconWithLabel
          class="ps-2"
          image-name="/svg/info_circle.svg"
          before
        >
          <div>
            {{
              $t('start-calculation.vehicleInfo.otherVehicleType', {
                type: funnelFromApi,
              })
            }}
            <a
              class="btn btn-outline-dark"
              :href="otherVehicleTypeLink(calculatedSoortFromApi, localKenteken)"
            >
              {{ $t('start-calculation.vehicleInfo.open') }} {{ funnelFromApi }}</a>
          </div>
        </BaseIconWithLabel>
      </BaseToastMessage>
    </div>
    <div v-if="wrongLicensePlate">
      <BaseToastMessage
        hide-arrow
        color="warning"
      >
        <BaseIconWithLabel
          class="ps-2"
          image-name="/svg/info_circle.svg"
          before
        >
          {{ $t('start-calculation.vehicleInfo.licensePlateUnknown') }}
        </BaseIconWithLabel>
      </BaseToastMessage>
    </div>
    <div v-if="!props.voertuig.type_id_correct">
      <BaseToastMessage
        hide-arrow
        color="warning"
      >
        {{ $t('start-calculation.vehicleInfo.type_id_warning') }}
      </BaseToastMessage>
    </div>
  </div>
</template>
